import React from "react";
import { useIntl } from "react-intl";
import IComponentShortage from "../../../models/IComponentShortage";
import { OwIconTooltip } from "../../shared/OwIconTooltip/OwIconTooltip";

type DetailsProps = {
    shortage: IComponentShortage;
};

/** Tooltip body: outstanding demand, available, the minimum to order, and which finished SKUs drive it. */
export const ComponentShortageDetails = ({ shortage }: DetailsProps) => {
    const intl = useIntl();

    return (
        <div className={"text-wrap text-start"} data-testid="component-shortage-details">
            <div className={"fw-bold mb-1"}>{intl.formatMessage({ id: "component_shortage", defaultMessage: "Component shortage" })}</div>
            <div>
                {intl.formatMessage(
                    { id: "component_outstanding_demand", defaultMessage: "Outstanding demand: {qty}" },
                    { qty: shortage.demand }
                )}
            </div>
            <div>
                {intl.formatMessage({ id: "component_available_qty", defaultMessage: "Available: {qty}" }, { qty: shortage.available })}
            </div>
            {shortage.shortfall > 0 ? (
                <div className={"fw-bold"}>
                    {intl.formatMessage({ id: "order_at_least_n", defaultMessage: "Order at least {qty}" }, { qty: shortage.shortfall })}
                </div>
            ) : null}
            {shortage.drivers.length ? (
                <ul className={"mb-0 mt-1 ps-3"}>
                    {shortage.drivers.map((driver) => (
                        <li key={driver.parentCatalogItemId}>
                            {intl.formatMessage(
                                { id: "component_shortage_driver", defaultMessage: "{sku}: {units} units need {qty}" },
                                { sku: driver.parentSku, units: driver.units, qty: driver.demand }
                            )}
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};

type Props = {
    shortage?: IComponentShortage | null;
    inModal?: boolean;
};

/**
 * Warning icon for a BOM component that unreleased serials need more of than is available (OR-1460).
 * Renders nothing unless there is a shortfall. On-screen only.
 */
export const ComponentShortageWarning = ({ shortage, inModal }: Props) => {
    const intl = useIntl();

    if (!shortage || !(shortage.shortfall > 0)) {
        return null;
    }

    return (
        <span className={"ComponentShortageWarning ms-2"} data-testid="component-shortage-warning">
            <OwIconTooltip
                iconClassName={"fas fa-exclamation-triangle text-warning"}
                tooltipContent={<ComponentShortageDetails shortage={shortage} />}
                inModal={inModal}
                // Focusable so keyboard users can open the breakdown (the tooltip also opens on focus).
                additionalIconProps={{
                    "aria-label": intl.formatMessage({ id: "component_shortage", defaultMessage: "Component shortage" }),
                    role: "img",
                    tabIndex: 0,
                }}
            />
        </span>
    );
};

export default ComponentShortageWarning;
